/**
 * `ConversationSchema` — public read-API response shape for a conversation.
 * Derived from `ConversationDocumentShape` (see
 * `apps/services/conversation/src/domain/entities/conversation/conversation_document.ts`)
 * with server-only / denormalized internal fields stripped.
 *
 * Consumers: the conversation service `api/` layer maps
 * `ConversationDocument` to this schema before responding. `apps/clients/web`
 * imports it from `@voll-intelligence/domain` for client-side parsing.
 */
import { z } from 'zod';

import { ChatChannelSchema } from '../card/card';
import { LanguageCodeSchema } from '../_shared/language';
import { HumanDeskSchema } from '../desk/desk_commands';
import { ContactRefSchema } from './contact_ref';
import { ConversationIdSchema, MessageDirectionSchema } from './message_envelope';
import { EnvelopeOrgIdSchema } from './org_id';
import { ConversationStateSchema } from './state';

/**
 * Public summary of one conversation↔trip link (many-to-many). Projected from
 * the conversation service's `conversation_trip_links` collection. The full
 * link document (source/history/message refs) is server-only; consumers see
 * only this. `relation` is a plain string (open provenance vocabulary owned by
 * the conversation service) — not an enum here, to avoid a cross-package enum
 * duplicate.
 */
export const ConversationTripLinkSummarySchema = z.object({
  tripId: z.string().min(1).max(100),
  relation: z.string().min(1).max(50),
  status: z.enum(['active', 'detached']),
  updatedAt: z.coerce.date(),
});

export type ConversationTripLinkSummary = z.infer<typeof ConversationTripLinkSummarySchema>;

export const ConversationSchema = z.object({
  id: ConversationIdSchema,
  // OMITTED: _schemaVersion (server-only storage version)
  orgId: EnvelopeOrgIdSchema,
  channel: ChatChannelSchema,
  // OMITTED: contactId (denorm internal — public surface is contactRef.externalId)
  contactRef: ContactRefSchema,
  state: ConversationStateSchema,
  lastMessageAt: z.coerce.date().optional(),
  lastMessageDirection: MessageDirectionSchema.optional(),
  // No .default(0) here — the response always carries the value materialized
  // from the document; defaults exist only on the storage shape.
  messageCount: z.number().int().min(0),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  resolvedAt: z.coerce.date().optional(),
  // Translation (feature 2026-06): detected customer language (BCP-47), set on
  // the first inbound detect; and the operator's OUTBOUND-translation toggle.
  customerLanguage: LanguageCodeSchema.optional(),
  translationMode: z.enum(['off', 'on']).optional(),
  // Desk bridge (2026-09): set when the escalation opened a Zendesk desk — the
  // web hides the operator composer while a Zendesk agent serves the traveler.
  humanDesk: HumanDeskSchema.optional(),
  // Trip association is optional because conversations may start generic and
  // only become trip-scoped after intent resolution or an explicit attach.
  // `tripId` = current focus / compat (first non-null trip wins); NOT the
  // source of truth for grouping — use `tripLinks` for that.
  tripId: z.string().min(1).max(100).optional(),
  // Many-to-many conversation↔trip links (a conversation can touch several
  // trips). Optional so mutation echoes that project a raw document without a
  // join still parse; the list/read endpoints always populate it (possibly []).
  tripLinks: z.array(ConversationTripLinkSummarySchema).optional(),
});

export type Conversation = z.infer<typeof ConversationSchema>;

// ─────────────────────────────────────────────────────────────────────────
// Outbound (operator → customer) translation policy. Shared by the web toggle
// and the conversation send path so both agree on one rule.

/** Operator language in v1 (single-tenant). Outbound is translated only when the
 * customer speaks something else. Internal default for the helpers below. */
const OPERATOR_LANG = 'pt-BR';

/** Primary (base) language subtag, lowercased — `'pt-BR'` → `'pt'`, `'EN'` → `'en'`. */
function baseLang(code: string): string {
  const dash = code.indexOf('-');
  return (dash === -1 ? code : code.slice(0, dash)).toLowerCase();
}

/**
 * The customer speaks a language other than the operator's, compared by base
 * subtag (so `'pt'` and `'pt-BR'` are the same language). An `undefined`
 * (not-yet-detected) language is NOT foreign — there is nothing to translate to
 * yet. Also used to decide whether the outbound-translation toggle is relevant.
 */
export function isForeignCustomer(
  customerLanguage: string | undefined,
  operatorLang: string = OPERATOR_LANG,
): boolean {
  return customerLanguage !== undefined && baseLang(customerLanguage) !== baseLang(operatorLang);
}

/**
 * Whether operator → customer (outbound) translation is effectively ON.
 *
 * OPT-OUT for foreign customers: ON whenever the customer's language differs
 * from the operator's, UNLESS the operator explicitly set it `'off'`. This stops
 * an operator from accidentally sending a Portuguese reply to (e.g.) an
 * English-speaking customer just because they never flipped a toggle. For a
 * same-language or undetected customer it is always OFF (translating PT→PT is a
 * no-op).
 */
export function isOutboundTranslationOn(
  translationMode: 'on' | 'off' | undefined,
  customerLanguage: string | undefined,
  operatorLang: string = OPERATOR_LANG,
): boolean {
  return isForeignCustomer(customerLanguage, operatorLang) && translationMode !== 'off';
}
